import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { api, unwrap } from "@/api/client";
import { keys, type SystemInfo, systemInfoQuery } from "@/api/queries";
import { Button } from "@/design/button";
import { Field, Input } from "@/design/form";
import { AuthLayout } from "@/features/auth/AuthLayout";
import { errorMessage, fieldErrors } from "@/lib/errors";

export const Route = createFileRoute("/setup")({
  beforeLoad: async ({ context }) => {
    const info = await context.queryClient.fetchQuery(systemInfoQuery);
    if (info.edition === "local" || !info.setup_required) throw redirect({ to: "/" });
  },
  component: SetupPage,
});

const schema = z
  .object({
    orgName: z.string().trim().min(2),
    orgCode: z.string().regex(/^[A-Z0-9]{2,10}$/, "auth.orgCodeHelp"),
    displayName: z.string().trim().min(1),
    username: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/, "auth.usernameHelp"),
    password: z.string().min(8, "errors.password.too_short"),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { path: ["confirm"], message: "auth.passwordMismatch" });
type Values = z.infer<typeof schema>;

function SetupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { orgName: "", orgCode: "", displayName: "", username: "", password: "", confirm: "" },
  });
  const { errors, isSubmitting } = form.formState;
  const msg = (m?: string) => (m ? t(m, { defaultValue: m }) : undefined);

  async function submit(v: Values) {
    try {
      const me = await unwrap(
        api.POST("/api/v1/setup", {
          body: {
            organization: { name: v.orgName, code: v.orgCode },
            admin: { username: v.username, display_name: v.displayName, password: v.password },
          },
        }),
      );
      queryClient.setQueryData(keys.me, me);
      // The cached system info still says setup is required; update it before the route guards run.
      queryClient.setQueryData<SystemInfo>(keys.system, (old) => old && { ...old, setup_required: false });
      await navigate({ to: "/users" });
    } catch (e) {
      const fields = fieldErrors(e);
      if (fields.new_password || fields["admin.password"]) form.setError("password", { message: errorMessage(t, e) });
      else form.setError("root", { message: errorMessage(t, e) });
    }
  }

  return (
    <AuthLayout title={t("auth.setupTitle")}>
      <form className="w-full max-w-[420px]" onSubmit={form.handleSubmit(submit)} noValidate>
        <h1 className="m-0 mb-1.5 text-2xl font-[650]">{t("auth.setupTitle")}</h1>
        <p className="m-0 mb-6 text-ink-3">{t("auth.setupIntro")}</p>
        <fieldset className="m-0 mb-5 flex flex-col gap-3.5 border-0 p-0">
          <legend className="mb-2 text-xs font-semibold tracking-[0.06em] text-ink-3 uppercase">{t("auth.setupOrg")}</legend>
          <Field label={t("auth.orgName")} required error={msg(errors.orgName?.message)}>
            {(p) => <Input {...p} {...form.register("orgName")} />}
          </Field>
          <Field label={t("auth.orgCode")} required help={t("auth.orgCodeHelp")} error={msg(errors.orgCode?.message)}>
            {(p) => (
              <Input
                className="font-mono uppercase"
                maxLength={10}
                {...p}
                {...form.register("orgCode", { setValueAs: (v: string) => v.toUpperCase() })}
              />
            )}
          </Field>
        </fieldset>
        <fieldset className="m-0 flex flex-col gap-3.5 border-0 p-0">
          <legend className="mb-2 text-xs font-semibold tracking-[0.06em] text-ink-3 uppercase">{t("auth.setupAdmin")}</legend>
          <Field label={t("auth.displayName")} required error={msg(errors.displayName?.message)}>
            {(p) => <Input autoComplete="name" {...p} {...form.register("displayName")} />}
          </Field>
          <Field label={t("auth.usernameOnly")} required help={t("auth.usernameHelp")} error={msg(errors.username?.message)}>
            {(p) => <Input autoComplete="username" {...p} {...form.register("username")} />}
          </Field>
          <Field label={t("auth.password")} required help={t("auth.passwordHelp")} error={msg(errors.password?.message)}>
            {(p) => <Input type="password" autoComplete="new-password" {...p} {...form.register("password")} />}
          </Field>
          <Field label={t("auth.confirmPassword")} required error={msg(errors.confirm?.message)}>
            {(p) => <Input type="password" autoComplete="new-password" {...p} {...form.register("confirm")} />}
          </Field>
        </fieldset>
        {errors.root && (
          <p role="alert" className="mt-4 mb-0 text-[13px] text-danger">
            {errors.root.message}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" className="mt-6 w-full" disabled={isSubmitting}>
          {t("auth.finishSetup")}
        </Button>
      </form>
    </AuthLayout>
  );
}
